import { Component, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin } from 'rxjs';
import { CreateEventModalPage } from './modals/create-event-modal/create-event-modal.page';
import { AppUser } from './models/appUser.model';
import { Portal } from './models/portal.model';
import { AppuserService } from './services/appuser/appuser.service';
import { AuthenticationService } from './services/authentication/authentication.service';
import { PortalService } from './services/portal/portal.service';
import { StorageService } from './services/storage/storage.service';
import { DeviceService } from './services/device/device.service';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { PreviousRouteService } from './services/previousRoute/previous-route.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AppDevice } from './models/appDevice.model';
import { RecruitMemberService } from './services/recruitMember/recruit-member.service';
import constants from 'src/app/constants';
import { UpdateProfilePopoverComponent } from './popovers/update-profile-popover/update-profile-popover.component';
import { AppSettingsService } from './services/app-settings/app-settings.service';
import { RevenuecatService } from './services/revenuecat/revenuecat.service';
import OneSignal from 'onesignal-cordova-plugin';
import { CompanionService } from './services/companions/companion.service';
import { AppInfoService } from './services/appInfo/app-info.service';
import { DataService } from './services/data/data.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  routeSubscription: Subscription;

  appUser: AppUser = new AppUser();

  navigationOptions = [];

  isPortalPage: boolean;
  portal: Portal = new Portal();

  portals: Portal[] = [];

  lastRoute: string;

  platformName: string;

  constants = constants;

  navigatedToUserSettings: boolean;

  portalSubscription: Subscription;
  portalCheckTime: number

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private device: Device,
    public platform: Platform,
    private storage: StorageService,
    public translate: TranslateService,
    private portalService: PortalService,
    private activatedRoute: ActivatedRoute,
    private appUserService: AppuserService,
    private menuController: MenuController,
    private modalController: ModalController,
    private authentication: AuthenticationService,
    private deviceService: DeviceService,
    private previousRoute: PreviousRouteService,
    private recruitMembersService: RecruitMemberService,
    private popoverController: PopoverController,
    private settings: AppSettingsService,
    private revenuecatService: RevenuecatService,
    private companionService: CompanionService,
    private appInfoService: AppInfoService,
    private dataService: DataService
  ) {
      this.navigatedToUserSettings = false;
      this.initializeApp();

      this.translate.setDefaultLang("en");
      this.translate.use(this.translate.getBrowserLang() || "en");

      this.previousRoute.initializePreviousRoute();

      this.router.events.subscribe(() => {
        this.settings.getAppSettings().subscribe(() => {
          if(location.hash != this.lastRoute) {
            this.routeSubscription = this.activatedRoute.paramMap.subscribe(paramMap => { // Executed every route change. Populate the side menu's navigation options & set global theme
              if (!this.portalSubscription) {
                this.portalCheckTime = null;
                this.portalSubscription = this.portalService.portal.subscribe(([portal, timePageLoaded]) => {
                  if (!this.portalCheckTime || this.portalCheckTime < timePageLoaded) {
                    this.portalCheckTime = timePageLoaded;
                    if(portal) {
                      this.portalService.getPortalByPortalID(portal.id).subscribe((portal: Portal) => {
                          this.portal = portal;
                          this.isPortalPage = true;
                      });
                    } else {
                      this.isPortalPage = false;
                    }
                  }
                });
              }

              this.storage.get("Session_Token").then(sessionToken => {
                if(sessionToken) {
                  this.appUserService.getAppUser().then((appUser: AppUser) => {
                    if(appUser && appUser.id) {
                      this.appUser = appUser;

                      this.revenuecatService.configRevenueCat(this.appUser);

                      this.platformName = "web"; // Cordova doesn't work on browser. Manually set platform to "web"
                      if (this.platform.is('cordova')) {
                          this.platformName = this.device.platform;
                      }

                      forkJoin([
                          this.deviceService.generateDeviceId(this.platformName, this.appUser.id.toString()),
                          this.portalService.getPortals(),
                          this.recruitMembersService.getRecruitMember()
                      ]).subscribe(([id, portals, recruitMember]) => {
                          this.deviceService.getDevice(id, this.appUser.id).then((appDevice: AppDevice) => {
                            if (!appDevice) {
                                this.addDevice(this.platformName, this.appUser.id, id);
                            } else if (this.platform.is('cordova')) {
                                this.deviceService.oneSignalInit(appDevice);
                            }
                          });

                          this.portals = portals;
                          this.portals.forEach(portal => {
                            this.portalService.getPortalRules(portal.id).subscribe();
                          });
                          
                          this.navigationOptions = [];
      
                          this.navigationOptions.push({ icon: "icons/home.svg", label: this.translate.instant("MENU.dashboard"), path: "/home/dashboard", htmlId: "navigation-dashboard" });
                          this.navigationOptions.push({ icon: "icons/wrench.svg", label: this.translate.instant("PROFILE.profileSettings"), path: "/home/settings", htmlId: "navigation-account-settings" });
                          this.navigationOptions.push({ icon: "icons/user-circle-single.svg", label: this.translate.instant("PROFILE.viewProfile"), path: `/home/profile/${this.appUser.id}`, htmlId: "navigation-profile" });
                          this.navigationOptions.push({ icon: "icons/recruits.svg", label: this.translate.instant("MENU.recruits"), path: "/home/recruitment-dashboard" });

                          if(recruitMember?.id || this.appUser.accountConnected) {
                            this.navigationOptions.push({ icon: "icons/connections.svg", label: this.translate.instant("PEOPLE.connections"), path: "/home/connections" });
                          }

                          forkJoin([
                            this.companionService.getChildrenForAppUser(),
                            this.companionService.getPendingCompanionsForAppUser()
                          ]).subscribe(([children, pending]) => {
                            if((children && children.length > 0) || (pending && pending.length > 0)) {
                              this.navigationOptions.push({ icon: "icons/people-outline.svg", label: this.translate.instant('COMPANIONS.companionAccounts'), path: "/home/companion-accounts" });
                            }
                          });
                      });
                    }
                  });
                }
              });
            });
          }
        this.lastRoute = location.hash;
      });
    });
  }

  async updateProfile(progress: number) {
    const popover = await this.popoverController.create({
      component: UpdateProfilePopoverComponent,
      componentProps: {
        progress: progress
      },
      cssClass: 'popover-recruit-info',
      
    });
    await popover.present();
  }

  /* 
    Used to initialize a lister for deep links (known as universal links (iOS) and App Links (Android)). They allow an app to be opened
    when a link that would bring you to the domain is clicked on (example: user A sends user B a link to a post and user B has the app installed.
    When the link is clicked, rather than loading the website the app is opened).

    This code is generic for the app but special steps must be taken for both Android and iOS in order for deep links to work. 

    For more reading / tutorial: https://capacitorjs.com/docs/guides/deep-links
  */
  initializeApp() {
    this.platform.backButton.subscribeWithPriority(10, () => { // Called when the device's hardware back button is hit
      this.router.navigateByUrl(this.lastRoute);
    });

    this.appInfoService.setAppInfo();

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.ngZone.run(() => {
        // Example url: https://app.1rink.com/#/home/dashboard
        // slug = /tabs/tab2
        const slug = event.url.split("#").pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }

    if (this.portalSubscription) {
      this.portalSubscription.unsubscribe();
    }
  }

  addDevice(platform: string, userID: number, deviceID: string) {
    this.deviceService.getDeviceType(platform).subscribe(deviceTypeId => {
      this.deviceService.addDevice(userID, deviceID, deviceTypeId).subscribe(() => {
        this.deviceService.getDevice(deviceID, userID).then((appDevice: AppDevice) => {
          this.deviceService.oneSignalInit(appDevice);
        });
      });
    });
  }

  goToNotifications() {
    this.router.navigate(['/home/notifications']).then(()=> {
      this.menuController.close();
    });
  }

  goToPortal(portalID: number) {
    const portal = this.portals.find((portal: Portal) => portal.id === portalID);
    if (portal?.isPortalMember) {
      this.router.navigate(['/home/portal/' + portalID]).then(() => {
        this.menuController.close();
      });
    } else if (portal?.isGlobal) {
      // if portal is global, navigate to dashboard and set the portal scroller portal
      this.dataService.selectPortalScrollerPortal(portalID);
      this.router.navigate(['/home/dashboard']).then(() => {
        this.menuController.close();
      });
    } else {
      // not a member of the portal, do not navigate
      return;
    }
  }

  async openCreateEventModal() {
    const modal = await this.modalController.create({
      component: CreateEventModalPage,
      canDismiss: true,
      cssClass: 'one-rink-modal'
    });

    return await modal.present();
  }

  navigate(navigationOption: any) {
    if (navigationOption.path === '/home/dashboard') {
      this.dataService.selectPortalScrollerPortal(null); // Reset the portal scroller portal when navigating to the dashboard
    }
    this.router.navigate([navigationOption.path]);
    this.menuController.close('menu');
  }

  logout() {
    this.authentication.logout().then(() => {
      this.menuController.close();
      if (this.platform.is('cordova')) {
        OneSignal.User.pushSubscription.optOut();
        OneSignal.logout();
      }
      this.router.navigate(['/login']);
    });
  }
}
 